import { useToast } from "@chakra-ui/toast";
import { FormikHelpers } from "formik";
import { FC, useCallback } from "react";
import { ConnectionHandler, graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import { ScheduleCreateFormContainer_Mutation } from "~/src/__relay_artifacts__/ScheduleCreateFormContainer_Mutation.graphql";
import { ScheduleCreateFormContainer_Query } from "~/src/__relay_artifacts__/ScheduleCreateFormContainer_Query.graphql";
import { useFormErrorHandler } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";
import { formatTimeToISO } from "~/src/lib/utils/date-utils";

import { ScheduleForm, ScheduleFormValues } from "./";

type Props = {
  siteSlug: string;
};

const query = graphql`
  query ScheduleCreateFormContainer_Query($siteSlug: String!) {
    site(slug: $siteSlug) {
      owner {
        id
      }
    }
  }
`;

const mutation = graphql`
  mutation ScheduleCreateFormContainer_Mutation(
    $input: AddScheduleInput!
    $connections: [ID!]!
  ) {
    addSchedule(input: $input) {
      scheduleEdge @prependEdge(connections: $connections) {
        node {
          id
          slug
          startTime
          endTime
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const ScheduleCreateFormContainer: FC<Props> = ({ siteSlug }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { onFormError } = useFormErrorHandler();
  const { site } = useLazyLoadQuery<ScheduleCreateFormContainer_Query>(query, {
    siteSlug,
  });

  const addScheduleMutation =
    useMutationCommit<ScheduleCreateFormContainer_Mutation>(mutation);

  const initialValues: ScheduleFormValues = {
    startTime: null,
    endTime: null,
  };

  const handleSubmit = useCallback(
    async (
      values: ScheduleFormValues,
      { setErrors }: FormikHelpers<ScheduleFormValues>
    ) => {
      try {
        const connectionId = ConnectionHandler.getConnectionID(
          site.owner.id,
          "ScheduleListTab_schedules",
          { orderBy: { direction: "ASC", field: "START_TIME" } }
        );
        const { addSchedule } = await addScheduleMutation({
          variables: {
            connections: [connectionId],
            input: {
              siteSlug,
              startTime: formatTimeToISO(values.startTime),
              endTime: formatTimeToISO(values.endTime),
            },
          },
        });
        if (!addSchedule?.scheduleEdge?.node)
          throw new Error("assertion failed");
        toast({ title: "スケジュールを作成しました", status: "success" });
        navigate(`/sites/${siteSlug}/settings?tab=schedules`);
      } catch (err) {
        onFormError(err as any, setErrors);
      }
    },
    [addScheduleMutation, navigate, onFormError, site.owner.id, siteSlug, toast]
  );

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/settings?tab=schedules`);
  }, [navigate, siteSlug]);

  return (
    <ScheduleForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};
